import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Action, Condition } from '@cbre/rule-engine';
import { collapse } from '@shared/functions';
import { EntityType } from '@shared/models/entity-enum';
import { Rule as RuleDef, RuleDictionary } from '@shared/models/rules.model';
import { LookupRefService } from '@shared/services/lookup-ref.service';
import { map, Observable } from 'rxjs';

interface PartialRule<T> {
  when: Pick<Condition<T>, 'params' | 'type'>[];
  then: Pick<Action<T>, 'params' | 'type'>[];
}

@Injectable({
  providedIn: 'root',
})
export class ControlRulesResolver {
  private entity: EntityType;

  constructor(private lookupRefService: LookupRefService) {}

  resolve<T = any>(route: ActivatedRouteSnapshot): Observable<{ [key: string]: PartialRule<T> }> {
    return this.configure({ entity: route.params.entity }).loadRules<T>();
  }

  public configure({ entity }) {
    this.entity = entity;
    return this;
  }

  loadRules<T>(): Observable<{ [key: string]: PartialRule<T> }> {
    return this.lookupRefService.getEntityRules(this.entity).pipe(
      map((res: RuleDictionary) => {
        const ruleDefs = Object.entries(res).map(([id, ruleDef]) => ({
          [id]: this.toRuleDef<T>(ruleDef),
        }));
        return collapse<{ [key: string]: PartialRule<T> }>(ruleDefs);
      })
    );
  }

  private toRuleDef<T>({ conditions, actions, ...rest }: RuleDef): PartialRule<T> {
    return { ...rest, when: conditions, then: actions };
  }
}
